import _extends from '@babel/runtime/helpers/extends';
import React__default, { useState, useContext } from 'react';
import PT from 'prop-types';
import styled, { css } from 'styled-components';
import { A as Alert } from '../../Alert/Alert-BsIFtdME.js';
import { I as Icon } from '../../Icon/Icon-CCTOGwuW.js';
import { F as FileUploadContext } from '../FileUploadContext-CI9_em1Z.js';
import { u as upload } from '../../LocalModules/storm-ui-icons/dist/esm/localIcons-C0k5YASI-B7bhIt-r.js';
import { T as Text } from '../../Text/Text-DpPLcIqD.js';
import { n as noop } from '../../LocalModules/storm-ui-utils/dist/esm/noop-BNkHXPFl-BNkHXPFl.js';

const transition = property => css(["@media (prefers-reduced-motion:no-preference){transition:", " 100ms ease-in-out;}"], property);
const Container = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-30053921__sc-mb1eqy-0"
})(["background:", ";display:", ";width:", ";cursor:", ";"], ({
  theme
}) => theme.palette.white, ({
  $fullWidth
}) => $fullWidth ? 'block' : 'inline-block', ({
  $fullWidth
}) => $fullWidth ? '100%' : 'auto', ({
  disabled
}) => disabled && 'not-allowed');
const Label = /*#__PURE__*/styled('label').withConfig({
  componentId: "sc-storm-ui-30053921__sc-mb1eqy-1"
})(["border:1px dashed ", ";border-radius:6px;cursor:pointer;width:100%;height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;margin-bottom:0;outline:none;box-sizing:border-box;padding:", ";position:relative;transition:all 0.3s;vertical-align:middle;", " &:focus{outline:2px solid ", ";outline-offset:2px;", "}&:hover{", " border-color:", ";background:", ";&& > i{", " color:", ";}}", " ", ""], ({
  theme
}) => theme.palette.gray[500], ({
  theme
}) => theme.spacing.base, transition('background'), ({
  theme
}) => theme.palette.blue[700], ({
  disabled
}) => !disabled && css(["", " background:", ";border-color:", ";&& > i{", " color:", ";}"], transition('color'), ({
  theme
}) => theme.palette.blue[50], ({
  theme
}) => theme.palette.blue[700], transition('color'), ({
  theme
}) => theme.palette.gray[700]), transition('background'), ({
  theme
}) => theme.palette.blue[700], ({
  theme
}) => theme.palette.blue[50], transition('color'), ({
  theme
}) => theme.palette.gray[700], ({
  disabled
}) => disabled && css(["border-color:", ";background:", ";pointer-events:none;&& > p,div{color:", ";}&& > i{", " color:", ";}"], ({
  theme
}) => theme.palette.gray[300], ({
  theme
}) => theme.palette.gray[25], ({
  theme
}) => theme.typography.color.tertiary, transition('color'), ({
  theme
}) => theme.palette.gray[300]), ({
  activeDrag
}) => activeDrag && css(["", " border-color:", ";background:", ";&& > i{", " color:", ";}"], transition('background'), ({
  theme
}) => theme.palette.blue[700], ({
  theme
}) => theme.palette.blue[50], transition('color'), ({
  theme
}) => theme.palette.blue[900]));
const Overlay = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-30053921__sc-mb1eqy-2"
})(["position:absolute;width:100%;height:100%;border-radius:6px;top:0px;right:0px;bottom:0px;left:0px;"]);
const UploadIcon = /*#__PURE__*/styled(Icon).withConfig({
  componentId: "sc-storm-ui-30053921__sc-mb1eqy-3"
})(["color:", ";", ""], ({
  theme
}) => theme.palette.gray[500], transition('color'));
const DropZoneTextContainer = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-30053921__sc-mb1eqy-4"
})(["text-align:center;margin-top:", ";"], ({
  theme
}) => theme.spacing.base);
const CriteriaListContainer = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-30053921__sc-mb1eqy-5"
})(["margin-top:", ";color:", ";"], ({
  theme
}) => theme.spacing.micro, ({
  theme
}) => theme.typography.color.secondary);
const UploaderAlert = /*#__PURE__*/styled(Alert).withConfig({
  componentId: "sc-storm-ui-30053921__sc-mb1eqy-6"
})(["margin-top:", ";"], ({
  theme
}) => theme.spacing.mini);
const FileUploadDropZone = ({
  heading,
  dropzoneId,
  onDrop
}) => {
  const [activeDrag, setActiveDrag] = useState(false);
  const {
    description,
    disabled,
    errorText,
    fullWidth,
    inputId,
    inputRef,
    onCloseAlert
  } = useContext(FileUploadContext);
  const dropZoneTextId = `${dropzoneId}-heading`;
  const criteriaListId = `${dropzoneId}-descriptions`;
  const handleKeyDown = event => {
    if (!disabled && event.key === 'Enter') {
      event.preventDefault();
      if (inputRef !== null && inputRef !== void 0 && inputRef.current) {
        /** Reset the value of the input so
         * duplicate files are not ignored */
        inputRef.current.value = '';
        inputRef.current.click();
      }
    }
  };
  const handleDrag = event => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === 'dragenter' || event.type === 'dragover') {
      setActiveDrag(true);
    } else if (event.type === 'dragleave') {
      setActiveDrag(false);
    }
  };
  const handleDrop = event => {
    event.preventDefault();
    setActiveDrag(false);
    onDrop === null || onDrop === void 0 || onDrop(event);
  };
  const handleClick = event => {
    event.preventDefault();
    if (inputRef !== null && inputRef !== void 0 && inputRef.current) {
      /** Reset the value of the input so
       * duplicate files are not ignored */
      inputRef.current.value = '';
      inputRef.current.click();
    }
  };
  return /*#__PURE__*/React__default.createElement(Container, {
    $fullWidth: fullWidth,
    disabled: disabled,
    id: dropzoneId
  }, /*#__PURE__*/React__default.createElement(Label, _extends({
    activeDrag: activeDrag,
    disabled: disabled
  }, disabled && {
    'aria-disabled': true
  }, {
    htmlFor: inputId,
    onDragEnter: handleDrag,
    onKeyDown: handleKeyDown,
    onClick: handleClick,
    tabIndex: 0,
    "aria-labelledby": dropZoneTextId,
    "aria-describedby": criteriaListId
  }), /*#__PURE__*/React__default.createElement(UploadIcon, {
    type: upload,
    size: "lg",
    "aria-hidden": true
  }), heading && /*#__PURE__*/React__default.createElement(DropZoneTextContainer, {
    id: dropZoneTextId
  }, heading), description && /*#__PURE__*/React__default.createElement(CriteriaListContainer, {
    id: criteriaListId
  }, description), activeDrag && /*#__PURE__*/React__default.createElement(Overlay, {
    onDragEnter: handleDrag,
    onDragLeave: handleDrag,
    onDragOver: handleDrag,
    onDrop: handleDrop
  })), errorText && /*#__PURE__*/React__default.createElement(UploaderAlert, {
    type: "error",
    role: "alert",
    withCloseButton: true,
    onClick: onCloseAlert
  }, /*#__PURE__*/React__default.createElement(Text, null, errorText)));
};
FileUploadDropZone.propTypes = {
  /**
  * Instructional text displayed within drop zone
  */
  heading: PT.node,
  /**
   * ID for drop zone
   */
  dropzoneId: PT.string.isRequired,
  /**
   * Callback function called when files are dropped in drop zone
   */
  onDrop: PT.func
};
FileUploadDropZone.defaultProps = {
  heading: undefined,
  onDrop: noop
};

export { FileUploadDropZone as F };
//# sourceMappingURL=FileUploadDropZone-ROyXq3ya.js.map
